import { useState, useEffect } from "react";
import { AudioLines, CheckCircle2, AlertCircle, Save, RefreshCw, PlugZap } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getTelephonySettings, saveTelephonySettings, testTelephony,
  type TelephonyServiceTest, type TelephonyStatus, type TelephonyTestResult,
} from "../../../../lib/calls";
import { Btn, StatusPill } from "../../../ui";
import { PopHost } from "../../../shell/Pop";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Note, SaveRow } from "../panel/fields";
import { SpeechProviderFields, type SpeechProviderDraft } from "./SpeechProviderFields";
import { forgetTtsVoices, useTtsVoices } from "./useTtsVoices";

/**
 * Settings -> Integrations: who does the speech work, and the keys they need.
 *
 * It started as one Gemini key and is now the tab where both speech decisions
 * are made - which service reads a finished recording, and which one speaks the
 * announcements. The two credentials live together because they are the same
 * question asked twice, and because keeping the ElevenLabs key on the tab that
 * also holds the synthesis switch is what stops "add a key" and "use the key"
 * being two pages apart.
 *
 * Both keys stay deliberately independent of the platform admin panel's AI
 * providers: saving here never touches integrations.google.apiKey and vice
 * versa, and nothing here counts against a tenant's AI credits.
 *
 * The status line asks about the CAPABILITY rather than about a vendor -
 * `providers.transcribeReady` - so it stops being green when somebody switches
 * to a provider whose key they have not pasted yet, which is exactly the mistake
 * this tab exists to catch.
 */
export function TranscriptionCard() {
  const t = useT();
  const [s, setS] = useState<TelephonyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [testState, setTestState] = useState<"idle" | "testing">("idle");
  const [testResult, setTestResult] = useState<TelephonyTestResult | null>(null);

  // The workspace's voices, for the default-voice picker. Shared with the
  // announcement pickers on the other tabs, so switching here and walking over
  // there does not fetch the same list twice.
  const { listing: voices } = useTtsVoices();

  const [draft, setDraft] = useState<SpeechProviderDraft>({
    transcribe: "gemini",
    tts: "tartunlp",
    geminiKey: "",
    geminiModel: "",
    geminiDailyCap: "0",
    geminiReserve: "0",
    elevenKey: "",
    elevenSttModel: "",
    elevenTtsModel: "",
    elevenVoiceId: "",
  });

  function hydrate(data: TelephonyStatus) {
    setS(data);
    setDraft({
      transcribe: data.providers.transcribe,
      tts: data.providers.tts,
      // Both secret fields are cleared on every load: the server never sends one
      // back, and an empty field on save means "keep the stored key".
      geminiKey: "",
      geminiModel: data.gemini.model,
      geminiDailyCap: String(data.gemini.dailyRequestCap ?? 0),
      geminiReserve: String(data.gemini.transcribeReserve ?? 0),
      elevenKey: "",
      elevenSttModel: data.elevenlabs.sttModel,
      elevenTtsModel: data.elevenlabs.ttsModel,
      elevenVoiceId: data.elevenlabs.voiceId,
    });
  }

  useEffect(() => {
    getTelephonySettings().then(hydrate).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      const updated = await saveTelephonySettings({
        providers: { transcribe: draft.transcribe, tts: draft.tts },
        gemini: {
          apiKey: draft.geminiKey,
          model: draft.geminiModel,
          // An empty field is zero, which is "no ceiling". `Number("")` is 0
          // already, but saying it here keeps the meaning of a cleared box from
          // depending on a JavaScript coercion nobody reading this would check.
          dailyRequestCap: Number(draft.geminiDailyCap) || 0,
          transcribeReserve: Number(draft.geminiReserve) || 0,
        },
        elevenlabs: {
          apiKey: draft.elevenKey,
          sttModel: draft.elevenSttModel,
          ttsModel: draft.elevenTtsModel,
          voiceId: draft.elevenVoiceId,
        },
      });
      hydrate(updated);
      // The voice list belongs to whichever service is switched on, and this
      // save is the one moment that can have changed. Without this an operator
      // who moves to ElevenLabs here and walks over to the announcements is
      // offered TartuNLP speakers for the rest of the session - every one of
      // them a value the new provider will refuse.
      forgetTtsVoices();
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  async function handleTest() {
    setTestState("testing");
    setTestResult(null);
    try {
      setTestResult(await testTelephony());
    } catch {
      setTestResult(null);
      setSaveState("error");
    } finally {
      setTestState("idle");
    }
  }

  if (loading) return <PanelLoader />;

  /**
   * One service's verdict, in the same two sentences for both.
   *
   * Most failure codes are machine words shown as they are - "auth_failed",
   * "network" - which is honest enough for a state whose next step is obvious.
   * The three synthesis-model verdicts are not: they name a field on THIS page
   * and a fix nobody would guess from the word "model_alpha", so those get a
   * sentence and the offending id beside it.
   */
  function verdict(name: string, res: TelephonyServiceTest | undefined) {
    if (!res) return null;
    const modelKeys: Record<string, string> = {
      model_alpha: "settings.transcription.testModelAlpha",
      model_unknown: "settings.transcription.testModelUnknown",
      model_not_tts: "settings.transcription.testModelNotTts",
    };
    const modelKey = res.error ? modelKeys[res.error] : undefined;
    return (
      <Note
        key={name}
        tone={res.ok ? "ok" : "bad"}
        icon={
          res.ok ? (
            <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
          ) : (
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
          )
        }
        live
      >
        {res.ok
          ? t("settings.transcription.testOk", { name, detail: res.detail ?? "" })
          : modelKey
            ? t(modelKey, { model: res.detail ?? "" })
            : t("settings.transcription.testFail", { name, error: res.error ?? "" })}
      </Note>
    );
  }

  return (
    // Every dropdown on this tab is a `Pop` menu now - the two provider choices,
    // the three model pickers and the voice - and Settings mounts no `PopHost`
    // of its own, so a `Pop` without one here would be a control that silently
    // never opens. It is also what keeps only one of the six up at a time.
    <PopHost>
      <IntegrationCard
        icon={AudioLines}
        title={t("settings.transcription.title")}
        desc={t("settings.transcription.desc")}
        status={
          <ConnStatus
            ok={!!s?.providers.transcribeReady}
            okLabel={t("settings.transcription.statusConfigured")}
            offLabel={t("settings.transcription.statusNotConfigured")}
          />
        }
      >
        <form onSubmit={handleSave} className="space-y-5">
          <SpeechProviderFields
            status={s}
            draft={draft}
            voices={voices}
            onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
          />

          {/* Both keys are reported, not only the one currently switched on. The
              order somebody works in is paste, test, then switch. */}
          {testResult ? (
            <div className="space-y-2">
              {verdict("Gemini", testResult.gemini)}
              {verdict("ElevenLabs", testResult.elevenlabs)}
            </div>
          ) : null}

          <SaveRow>
            <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
              {saveState === "saving" ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              {saveState === "saving"
                ? t("settings.transcription.saving")
                : t("settings.transcription.save")}
            </Btn>
            <Btn onClick={handleTest} disabled={testState === "testing"}>
              {testState === "testing" ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <PlugZap className="size-3.5" />
              )}
              {testState === "testing"
                ? t("settings.transcription.testing")
                : t("settings.transcription.test")}
            </Btn>
            {saveState === "saved" ? (
              <StatusPill tone="ok">{t("settings.transcription.saved")}</StatusPill>
            ) : null}
            {saveState === "error" ? (
              <StatusPill tone="lt">{t("settings.transcription.saveError")}</StatusPill>
            ) : null}
          </SaveRow>
        </form>
      </IntegrationCard>
    </PopHost>
  );
}
